/**
 * Fin de phase 1 en ordre par probabilité de rappel (design system, AlerteDeFinDePhase) et
 * bilan de séance (design system, BilanDeSeance).
 */
import { useEffect, useRef } from 'react';
import type { Note } from '../../fsrs/planificateur';
import { Bouton } from '../composants/Bouton';
import { Icone } from '../composants/Icone';
import { Touche } from '../composants/Touche';
import { decrireEcheance, dureeCourte } from '../format';
import { nombre, pluriel } from '../outils';
import { LIBELLES_NOTES } from './BarreDeNotation';

export function AlerteDeFinDePhase({
  seuil,
  cartesRevisees,
  cartesEnApprentissage,
  onContinuer,
  onTerminer,
}: {
  seuil: number;
  cartesRevisees: number;
  cartesEnApprentissage: number;
  onContinuer: () => void;
  onTerminer: () => void;
}) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => ref.current?.focus(), []);
  const avant = `${pluriel(cartesRevisees, 'carte')} ${cartesRevisees >= 2 ? 'avaient' : 'avait'} une probabilité de rappel inférieure à ${nombre(seuil)} %.`;
  const suite =
    cartesEnApprentissage > 0
      ? ` La séance continue avec ${cartesEnApprentissage >= 2 ? 'les' : 'la'} ${pluriel(cartesEnApprentissage, 'carte')} en apprentissage.`
      : ' Aucune carte en apprentissage ne revient dans la fenêtre de débordement.';
  return (
    <section
      ref={ref}
      aria-labelledby="alerte-titre"
      aria-describedby="alerte-texte"
      className="fc-carte-de-revision fc-alerte-de-fin-de-phase"
      tabIndex={-1}
    >
      <p className="fc-alerte-de-fin-de-phase__surtitre">
        <Icone nom="target" />
        Phase 1 terminée
      </p>
      <h1 className="fc-alerte-de-fin-de-phase__titre" id="alerte-titre">
        Toutes les cartes sous le seuil sont révisées
      </h1>
      <p className="fc-alerte-de-fin-de-phase__texte" id="alerte-texte">
        {avant}
        {suite}
      </p>
      <div className="fc-alerte-de-fin-de-phase__actions">
        {cartesEnApprentissage > 0 ? (
          <>
            <Bouton variante="principal" aria-keyshortcuts="Space" onClick={onContinuer}>
              Continuer <Touche>Espace</Touche>
            </Bouton>
            <Bouton onClick={onTerminer}>Terminer la séance</Bouton>
          </>
        ) : (
          <Bouton variante="principal" aria-keyshortcuts="Space" onClick={onTerminer}>
            Terminer la séance <Touche>Espace</Touche>
          </Bouton>
        )}
      </div>
    </section>
  );
}

export interface BilanDeSeanceProps {
  issue: 'terminee' | 'quittee';
  paquet: string;
  cartesRevisees: number;
  cartesDeLaSeance: number;
  /** Temps passé, en minutes. */
  duree: number;
  phases: number;
  repartition: Record<Note, number>;
  acquises: number;
  enApprentissage: number;
  prochaineEcheance: Date | null;
  onRevenir: () => void;
  onStatistiques: () => void;
  onReprendre?: () => void;
}

export function BilanDeSeance(p: BilanDeSeanceProps) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => ref.current?.focus(), []);
  const max = Math.max(1, ...Object.values(p.repartition));
  const phases =
    p.phases <= 1
      ? 'phase 1'
      : `phases ${[...Array(p.phases).keys()]
          .map((i) => i + 1)
          .join(', ')
          .replace(/, (\d+)$/, ' et $1')}`;
  let rang = 0;
  const anime = () => ({ ['--rang' as string]: rang++ });
  if (p.issue === 'quittee') {
    return (
      <section
        ref={ref}
        aria-labelledby="bilan-titre"
        className="fc-bilan-de-seance"
        data-entree
        tabIndex={-1}
      >
        <p className="fc-bilan-de-seance__surtitre fc-bilan-de-seance__anime" style={anime()}>
          <Icone nom="circle-pause" />
          Séance quittée
        </p>
        <h1
          className="fc-bilan-de-seance__titre fc-bilan-de-seance__anime"
          id="bilan-titre"
          style={anime()}
        >
          {`${nombre(p.cartesRevisees)} ${p.cartesRevisees >= 2 ? 'cartes' : 'carte'} sur ${nombre(p.cartesDeLaSeance)} ${p.cartesRevisees >= 2 ? 'révisées' : 'révisée'}`}
        </h1>
        <p className="fc-bilan-de-seance__texte fc-bilan-de-seance__anime" style={anime()}>
          Les réponses données sont enregistrées. Les cartes restantes vous attendent dans{' '}
          {p.paquet}.
        </p>
        <div className="fc-bilan-de-seance__actions">
          <Bouton variante="principal" onClick={p.onRevenir}>
            Revenir aux paquets <Touche>Entrée</Touche>
          </Bouton>
          {p.onReprendre && <Bouton onClick={p.onReprendre}>Reprendre la séance</Bouton>}
        </div>
      </section>
    );
  }
  return (
    <section
      ref={ref}
      aria-labelledby="bilan-titre"
      className="fc-bilan-de-seance"
      data-entree
      tabIndex={-1}
    >
      <p className="fc-bilan-de-seance__surtitre fc-bilan-de-seance__anime" style={anime()}>
        <Icone nom="circle-check" />
        Séance terminée
      </p>
      <h1
        className="fc-bilan-de-seance__titre fc-bilan-de-seance__anime"
        id="bilan-titre"
        style={anime()}
      >
        {`${pluriel(p.cartesRevisees, 'carte révisée', 'cartes révisées')} en ${dureeCourte(Math.max(1, p.duree))}`}
      </h1>
      <p className="fc-bilan-de-seance__texte fc-bilan-de-seance__anime" style={anime()}>
        {`${p.paquet}, ${phases}. `}
        {p.cartesRevisees === 0
          ? 'Aucune carte n’a été notée.'
          : [
              p.acquises > 0
                ? pluriel(p.acquises, 'carte est acquise', 'cartes sont acquises')
                : null,
              p.enApprentissage > 0
                ? p.enApprentissage >= 2
                  ? `${nombre(p.enApprentissage)} reviendront dans leurs étapes d’apprentissage`
                  : '1 reviendra dans ses étapes d’apprentissage'
                : null,
            ]
              .filter(Boolean)
              .join(' ; ') + '.'}
      </p>
      <dl aria-label="Répartition des réponses" className="fc-bilan-de-seance__repartition">
        <p
          aria-hidden="true"
          className="fc-bilan-de-seance__intitule fc-bilan-de-seance__anime"
          style={anime()}
        >
          Répartition des réponses
        </p>
        {([1, 2, 3, 4] as const).map((note) => {
          const style = anime();
          return (
            <div key={note} style={{ display: 'contents' }}>
              <dt className="fc-bilan-de-seance__note fc-bilan-de-seance__anime" style={style}>
                {LIBELLES_NOTES[note]}
              </dt>
              <dd
                aria-hidden="true"
                className="fc-bilan-de-seance__piste fc-bilan-de-seance__anime"
                style={style}
              >
                <span
                  className="fc-bilan-de-seance__barre"
                  style={{
                    ...style,
                    ['--part' as string]: Math.round((p.repartition[note] / max) * 100),
                  }}
                />
              </dd>
              <dd className="fc-bilan-de-seance__nombre fc-bilan-de-seance__anime" style={style}>
                {nombre(p.repartition[note])}
              </dd>
            </div>
          );
        })}
      </dl>
      {p.prochaineEcheance && (
        <p className="fc-bilan-de-seance__echeance fc-bilan-de-seance__anime" style={anime()}>
          <Icone nom="calendar" />
          Prochaine carte due {decrireEcheance(p.prochaineEcheance)}.
        </p>
      )}
      <div className="fc-bilan-de-seance__actions">
        <Bouton variante="principal" onClick={p.onRevenir}>
          Revenir aux paquets <Touche>Entrée</Touche>
        </Bouton>
        <Bouton icone="chart-column" onClick={p.onStatistiques}>
          Voir les statistiques
        </Bouton>
      </div>
    </section>
  );
}
